import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import FileSaver from "file-saver";
import { find, includes, isEmpty } from "lodash";
import styled from "styled-components";
import { privatApi } from "api/axios";
import { useGetPointManagerByUserQuery } from "api/baseAPI/pointManagers";
import { useGetUsersSelfQuery } from "api/baseAPI/user";
import {
  AllWalletsQueryParams,
  useGetAllWalletsQuery,
  Wallet,
} from "api/baseAPI/wallets";
import { Layout } from "components/layouts/Layout";
import { FilterBar } from "components/table/FilterBar";
import { StyledCell, StyledRow, Table } from "components/table/Table";
import { Box } from "UI/Box";
import { Button } from "UI/Button";
import { Grid } from "UI/Grid";
import { useFilterMaker } from "utils/filterMaker";
import { getColors } from "utils/getColors";
import { reduceParamsWithMultipleValues } from "utils/helpers";
import { ReactComponent as Download } from "utils/img/download.svg";
import { ReactComponent as LinkIcon } from "utils/img/link-icon.svg";
import { useUrlQuery } from "utils/url";
import { useSortMaker } from "hooks/sortMaker";
import { useNumberFormatter } from "hooks/useNumberFormatter";
import { setSelectedPointManager } from "reducers/basicSlice";
import { AllWalletsFilters } from "./parts/AllWalletsFilters";

export const AllWallets = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tableCellTitles = [
    t("Кошелек"),
    t("Статус"),
    t("Валюта"),
    t("Доступный баланс"),
    t("Общий баланс"),
    t("Роллинг резерв"),
    t("Овердрафт"),
    t("Холд"),
    t("Выплаты"),
  ];
  const sortColumns = [
    "",
    "currency",
    "available_balance_calc",
    "balance",
    "rolling_reserve_amount",
    "balance_min",
    "balance_hold",
    "status",
  ];

  const initParameters = {
    page: "1",
    page_size: "10",
    search: "",
    id__in: "",
    currency__in: "",
    point__in: "",
    ordering: "",
  };

  const [isDownloading, setIsDownloading] = useState(false);
  const { amountFormat } = useNumberFormatter();
  const { accentColor } = getColors();

  const getUsersSelfQuery = useGetUsersSelfQuery();
  const { data: pointManagersData } = useGetPointManagerByUserQuery(
    {
      user: getUsersSelfQuery?.data?.id || 0,
      page_size: "100",
    },
    { skip: !getUsersSelfQuery?.data?.id }
  );

  const { queryParams, querySetters } =
    useUrlQuery<AllWalletsQueryParams>(initParameters);

  const { page, page_size, search, ordering } = queryParams;

  const { set_page, set_page_size, set_search, set_ordering } = querySetters;

  const sort = useSortMaker(sortColumns, ordering, set_ordering);

  const filters = useFilterMaker(
    initParameters,
    queryParams,
    (values) => {
      set_page("1");
      querySetters.set_id__in(values.id__in);
      querySetters.set_currency__in(values.currency__in);
      querySetters.set_point__in(values.point__in);
    },
    () => {
      set_search(initParameters.search);
      querySetters.set_id__in(initParameters.id__in);
      querySetters.set_currency__in(initParameters.currency__in);
      querySetters.set_point__in(initParameters.point__in);
    }
  );

  const getAllWalletsId = useMemo(() => {
    if (queryParams.id__in) return queryParams.id__in;
    const filteredPointManagers = queryParams.point__in
      ? pointManagersData?.results?.filter((el) =>
          includes(queryParams.point__in.split(","), String(el.point_id))
        )
      : pointManagersData?.results;

    const allWalletIds = new Set<number>();
    filteredPointManagers?.forEach((manager) => {
      manager.available_wallets?.forEach((wallet) => {
        allWalletIds.add(wallet.id);
      });
    });

    return Array.from(allWalletIds).join(",");
  }, [pointManagersData, queryParams.id__in, queryParams.point__in]);

  const { data, isFetching, refetch } = useGetAllWalletsQuery(
    {
      ...queryParams,
      id__in: getAllWalletsId,
      point__in: "",
    },
    { skip: !getAllWalletsId }
  );

  const handleSetPage = (pageVal: number) => {
    set_page(String(pageVal));
  };

  const handleSetRowsPerPage = (rows: number) => {
    set_page("1");
    set_page_size(String(rows));
  };

  const handleSetSearch = (searchVal: string) => {
    set_page("1");
    set_search(searchVal);
  };

  const fetchExport = async (fileType: 1 | 2) => {
    const params = reduceParamsWithMultipleValues({
      id__in: getAllWalletsId,
      ...(queryParams.currency__in
        ? { currency__in: queryParams.currency__in }
        : {}),
    });

    return privatApi<void>(
      `/retriever/v0/wallets/export-to-file/`,
      "POST",
      {
        filters: params,
        export_type: fileType,
      },
      {
        responseType: "blob",
      }
    );
  };

  const handleDownload = async (fileType: 1 | 2) => {
    try {
      setIsDownloading(true);
      const response = await fetchExport(fileType);

      if (response.status === 204) {
        return;
      }
      let filename = fileType === 1 ? "wallets.xlsx" : "wallets.csv";
      try {
        const reg = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
          response?.headers?.["content-disposition"] || ""
        );
        if (reg) {
          filename = reg[1].replace(/['"]/g, "");
        }
      } catch (e) {
        console.error("Error", e);
      }

      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        FileSaver.saveAs(response.data, filename);
      } catch (e) {
        console.error("Error", e);
        toast.error("Ошибка");
      }
    } catch (err) {
      toast.error("Ошибка");
      throw err;
    } finally {
      setIsDownloading(false);
    }
  };

  const payoutWallets = useMemo(() => {
    if (!isEmpty(pointManagersData?.results)) {
      return (
        pointManagersData?.results.reduce(
          (acc, item) => ({
            ...acc,
            ...(item.payout_wallets?.length
              ? item.payout_wallets.reduce(
                  (accWallets, wallet) => ({
                    ...accWallets,
                    ...(!acc?.[wallet.id] &&
                    item.available_components?.find(
                      (el) => el.name === "payout_create"
                    )
                      ? { [wallet.id]: item.id }
                      : {}),
                  }),
                  {}
                )
              : {}),
          }),
          {} as Record<string, number>
        ) || {}
      );
    }
    return {};
  }, [pointManagersData]);

  const handleLinkPayouts = (id: number) => {
    const pointManager = find(
      pointManagersData?.results,
      (el) => el.id === payoutWallets?.[id]
    );
    if (!pointManager) return;
    dispatch(setSelectedPointManager({ ...pointManager }));
    navigate("/payout-create");
    navigate({
      pathname: "/payout-create",
      search: `?wallet_id=${id}`,
    });
  };

  const calculateAvailableBalance = (wallet: Wallet) => {
    const balance = Number(wallet.balance || 0);
    const balanceMin = Number(wallet.balance_min || 0);
    const hold = Number(wallet.balance_hold || 0);
    const rollingReserve = Number(wallet.rolling_reserve_amount || 0);
    return balance - balanceMin - hold - rollingReserve;
  };

  const calculateOverdraft = (wallet: Wallet) => {
    const balanceMin = Number(wallet.balance_min || 0);
    return balanceMin ? balanceMin * -1 : 0;
  };

  return (
    <Layout title={t("Балансы")}>
      <StyledTableBox>
        <Table
          tableCellTitles={tableCellTitles}
          isLoading={isFetching}
          filterBar={
            <FilterBar
              search={search}
              setSearch={handleSetSearch}
              refetch={refetch}
              customButton={
                <Grid
                  item
                  flex
                  sm={12}
                  xl={4}
                  responsive={{
                    xl: { ml: 16, mt: 0 },
                    sm: { ml: 0, mt: 16 },
                  }}
                >
                  <Box style={{ width: "calc(50% - 8px)" }}>
                    <Button
                      variant="outlined"
                      fullwidth
                      onClick={() => handleDownload(2)}
                      disabled={isDownloading}
                      iconStart={<Download />}
                    >
                      CSV
                    </Button>
                  </Box>
                  <Box style={{ width: "calc(50% - 8px)" }} ml={16}>
                    <Button
                      disabled={isDownloading}
                      onClick={() => handleDownload(1)}
                      fullwidth
                      variant="outlined"
                      iconStart={<Download />}
                    >
                      XLS
                    </Button>
                  </Box>
                </Grid>
              }
              filters={<AllWalletsFilters filters={filters} />}
            />
          }
          count={data?.count || 0}
          page={Number(page)}
          rowsPerPage={Number(page_size)}
          setPage={handleSetPage}
          setRowsPerPage={handleSetRowsPerPage}
          hideActionColumn
          sort={sort}
        >
          {data && !isEmpty(data.results)
            ? data.results.map((row) => (
                <React.Fragment key={row.id}>
                  <StyledRow>
                    <StyledCell>
                      <Box flex alignItems="center" nowrap>
                        {row.id} | {row.name}
                      </Box>
                    </StyledCell>
                    <StyledTd>
                      <StyledChip status={row.status === 1}>
                        {row.status === 1 ? t("Вкл") : t("Выкл")}
                      </StyledChip>
                    </StyledTd>
                    <StyledCell>{row.currency}</StyledCell>
                    <StyledTd>
                      {amountFormat(calculateAvailableBalance(row))}
                    </StyledTd>
                    <StyledTd>{amountFormat(row.balance || "")}</StyledTd>
                    <StyledTd>
                      {amountFormat(row.rolling_reserve_amount || "")}
                    </StyledTd>
                    <StyledTd>{amountFormat(calculateOverdraft(row))}</StyledTd>
                    <StyledTd>{amountFormat(row.balance_hold || "")}</StyledTd>
                    <StyledCell>
                      <StyledLinkIcon
                        disabled={!payoutWallets?.[row.id]}
                        onClick={() => handleLinkPayouts(row.id)}
                        data-tip
                        data-for={`link_payout_create${row.id}`}
                      />
                      {!payoutWallets?.[row.id] && (
                        <ReactTooltip
                          id={`link_payout_create${row.id}`}
                          border
                          place="top"
                          multiline
                          type="dark"
                          effect="solid"
                          borderColor={accentColor}
                        >
                          <span>
                            {t(
                              "Обратитесь к вашему менеджеру для настройки выплат"
                            )}
                          </span>
                        </ReactTooltip>
                      )}
                    </StyledCell>
                  </StyledRow>
                </React.Fragment>
              ))
            : null}
        </Table>
      </StyledTableBox>
    </Layout>
  );
};

const StyledTableBox = styled.div`
  padding-left: 24px;
  padding-right: 24px;
  padding-top: 32px;
  padding-bottom: 32px;

  & * td:nth-child(3),
  & * th:nth-child(3) {
    font-weight: bold;
  }

  & * th:nth-child(3) div,
  & * th:nth-child(4) div,
  & * th:nth-child(5) div,
  & * th:nth-child(6) div,
  & * th:nth-child(7) div {
    justify-content: flex-end !important;
    display: flex !important;
    width: 100% !important;
  }
`;
const StyledTd = styled(StyledCell)`
  text-align: right;
`;
const StyledLinkIcon = styled(LinkIcon)<{
  disabled: boolean;
}>`
  border: none;
  ${(props) =>
    props.disabled
      ? `
          path {
            stroke: #C8CFDC;
          }
          circle{
            fill: #C8CFDC;
          }
        `
      : "cursor: pointer;"}
`;

export const StyledChip = styled.div<{
  status: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 4px;
  height: 22px;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  margin: 0 15px;
  border-radius: 20px;
  background: ${(props) => (props.status ? "#3ea959" : "#d00025 ")};
  color: #fff;
  width: "max-content";
`;
